/** Kantenlänge der gerenderten Favicon-Kachel in Pixeln. */
const TILE_SIZE = 64;
const TILE_CORNER_RADIUS = 14;
const LETTER_FONT = 'bold 40px system-ui, -apple-system, sans-serif';
const DEFAULT_LETTER_COLOR = '#ffffff';
/** Markiert Links, die DIESE Logik erzeugt/übernommen hat (Eigentümerschaft). */
const OWNERSHIP_ATTRIBUTE = 'data-tenant-favicon';
/** Ursprüngliches href eines übernommenen fremden Links — für die Restaurierung. */
const RESTORE_HREF_ATTRIBUTE = 'data-tenant-favicon-restore';
/** Optische Mitte: minimal unter der geometrischen, wegen Versalhöhe. */
const LETTER_BASELINE_Y = TILE_SIZE / 2 + 2;

export interface TabIconIdentity {
  readonly tenantName?: string | undefined;
  readonly primaryColor: string;
  readonly letterColor?: string | undefined;
}

/**
 * Setzt das Tab-Icon (Favicon) auf eine **Buchstaben-Kachel**: Primärfarbe
 * der Praxis als Grund, darauf der Anfangsbuchstabe des Praxisnamens.
 *
 * Bewusst NICHT das Logo-Bild: Praxis-Logos liegen oft auf fremden Hosts
 * ohne CORS-Header (Canvas-Taint), sind für helle Hintergründe gestaltet
 * (auf dunklen Tab-Leisten unsichtbar) und als breite Wortmarken im
 * 16-px-Quadrat unleserlich. Die Buchstaben-Kachel ist rein clientseitig,
 * deterministisch für jeden Tenant und immer lesbar.
 *
 * Ohne Praxisnamen (optionales Feld) gilt das Browser-Default-Icon — lieber
 * neutral als eine leere Farbkachel. Das schließt den Übergang ein
 * (Review-Fund): war zuvor eine Tenant-Kachel gesetzt (z. B. Mock-Modus,
 * Tenant-Wechsel ohne Reload), wird sie entfernt bzw. ein zuvor vorhandenes
 * fremdes Favicon wiederhergestellt — dafür trägt der Link eine
 * Eigentümerschafts-Markierung.
 */
export function applyTenantFavicon(identity: TabIconIdentity, doc: Document = document): void {
  const letter = firstGrapheme(identity.tenantName);
  if (letter === null) {
    clearTenantFavicon(doc);
    return;
  }
  const tileDataUrl = drawLetterTile(letter, identity, doc);
  if (tileDataUrl === null) {
    return;
  }
  setFaviconHref(doc, tileDataUrl);
}

function firstGrapheme(tenantName: string | undefined): string | null {
  const trimmed = tenantName?.trim() ?? '';
  if (trimmed === '') {
    return null;
  }
  // Iterator statt charAt: zerlegt nach Codepoints (Umlaute, Emoji sicher).
  const [first] = trimmed;
  return first?.toLocaleUpperCase('de-DE') ?? null;
}

function drawLetterTile(letter: string, identity: TabIconIdentity, doc: Document): string | null {
  const canvas = doc.createElement('canvas');
  canvas.width = TILE_SIZE;
  canvas.height = TILE_SIZE;
  const context = canvas.getContext('2d');
  if (context === null) {
    return null;
  }
  paintBackground(context, identity.primaryColor);
  context.fillStyle = identity.letterColor ?? DEFAULT_LETTER_COLOR;
  context.font = LETTER_FONT;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(letter, TILE_SIZE / 2, LETTER_BASELINE_Y);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null; // Canvas nicht exportierbar (exotische Umgebung) → Default-Icon
  }
}

function paintBackground(context: CanvasRenderingContext2D, color: string): void {
  context.fillStyle = color;
  if (typeof context.roundRect === 'function') {
    context.beginPath();
    context.roundRect(0, 0, TILE_SIZE, TILE_SIZE, TILE_CORNER_RADIUS);
    context.fill();
    return;
  }
  context.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
}

function setFaviconHref(doc: Document, href: string): void {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link === null) {
    link = doc.createElement('link');
    link.rel = 'icon';
    doc.head.appendChild(link);
  } else if (!link.hasAttribute(OWNERSHIP_ATTRIBUTE)) {
    // Fremden (statischen) Link übernehmen: Original merken, damit ein
    // späterer Aufruf ohne Praxisnamen es wiederherstellen kann.
    link.setAttribute(RESTORE_HREF_ATTRIBUTE, link.getAttribute('href') ?? '');
  }
  link.setAttribute(OWNERSHIP_ATTRIBUTE, 'true');
  link.removeAttribute('type');
  link.href = href;
}

/** Entfernt die Tenant-Kachel bzw. restauriert ein übernommenes Original. */
function clearTenantFavicon(doc: Document): void {
  const link = doc.querySelector<HTMLLinkElement>(`link[rel="icon"][${OWNERSHIP_ATTRIBUTE}]`);
  if (link === null) {
    return; // Nichts von uns gesetzt — fremde Favicons nicht anfassen.
  }
  const originalHref = link.getAttribute(RESTORE_HREF_ATTRIBUTE);
  if (originalHref !== null && originalHref !== '') {
    link.href = originalHref;
    link.removeAttribute(OWNERSHIP_ATTRIBUTE);
    link.removeAttribute(RESTORE_HREF_ATTRIBUTE);
    return;
  }
  link.remove();
}
